/**
 * Shared queue for graph data requests so many heavy graph panels can load in tandem without
 * stampeding the API. Caps concurrent in-flight requests across ALL panels (Angular services are
 * singletons) and retries transient failures (timeouts / 5xx) with exponential backoff, so a request
 * that loses the load race no longer leaves a permanently blank panel.
 */

const MAX_CONCURRENT = 5;
const MAX_RETRIES = 2;
const BASE_BACKOFF_MS = 400;
const BACKOFF_FACTOR = 2;

/* @ngInject */
export default function GraphRequestQueue($q, $timeout) {
  const service = this;
  let active = 0;
  const waiting = [];

  service.enqueue = enqueue;

  // Enqueue `thunk` (a function returning a promise); resolves/rejects with its result.
  // opts.key: when provided, any NOT-YET-STARTED job with the same key is dropped (rejected as
  // { superseded: true }) in favour of this newer one — so a burst of same-target requests (e.g. a
  // user zooming repeatedly) coalesces to just the latest instead of piling up behind the concurrency cap.
  function enqueue(thunk, opts) {
    const key = opts && opts.key;
    return $q((resolve, reject) => {
      if (key) {
        for (let i = waiting.length - 1; i >= 0; i -= 1) {
          if (waiting[i].key === key) {
            const [dropped] = waiting.splice(i, 1);
            dropped.reject({ superseded: true });
          }
        }
      }
      waiting.push({ thunk, resolve, reject, key });
      pump();
    });
  }

  function pump() {
    while (active < MAX_CONCURRENT && waiting.length) {
      startJob(waiting.shift());
    }
  }

  function startJob(job) {
    active += 1;
    attempt(job.thunk, 0)
      .then(job.resolve, job.reject)
      .finally(onJobSettled);
  }

  function onJobSettled() {
    active -= 1;
    pump();
  }

  function attempt(thunk, tryNumber) {
    return $q.resolve()
      .then(thunk)
      .catch((err) => {
        // A deliberately cancelled request (superseded in-flight XHR, aborted via $http timeout) must
        // NOT be retried — it was replaced on purpose.
        if (err && err.xhrStatus === 'abort') {
          return $q.reject(err);
        }
        const status = err && err.status;
        // Retry only transient failures: timeout/network (status <= 0) or server errors (>= 500).
        const isTransient = !_isNumber(status) || status <= 0 || status >= 500; // eslint-disable-line no-magic-numbers
        if (tryNumber < MAX_RETRIES && isTransient) {
          const delay = BASE_BACKOFF_MS * (BACKOFF_FACTOR ** tryNumber);
          return $timeout(() => attempt(thunk, tryNumber + 1), delay);
        }
        return $q.reject(err);
      });
  }

  function _isNumber(value) {
    return typeof value === 'number' && !Number.isNaN(value);
  }
}
